import { useEffect, useMemo, useState } from "react"
import { useWindowSizeLocal } from "src/hooks/useWindowSize"
import { useIsAndroidApp, useIsIOSApp } from "./deviceType"

export type TWindowSize = {
  width: number | null
  height: number | null
  size?: string | null
}
export const breakpointValues = {
  sm: "30em",
  md: "48em",
  lg: "64em",
  xl: "80em",
  xxl: "96em",
  xxxl: "125em"
}
function parseBreakpoint(size: string) {
  const breakpointValue = breakpointValues?.[size]
  const breakpointToPx =
    Number.parseFloat(breakpointValue.split("em")?.[0]) * 16
  return breakpointToPx
}
export const isSm = (size: TWindowSize) => {
  return size.width < parseBreakpoint("md")
}
export const isMd = (size: TWindowSize) => {
  return (
    size.width >= parseBreakpoint("md") && size.width < parseBreakpoint("lg")
  )
}
export const isLg = (size: TWindowSize) => {
  return (
    size.width > parseBreakpoint("lg") && size.width < parseBreakpoint("xl")
  )
}
export const isXl = (size: TWindowSize) => {
  return (
    size.width > parseBreakpoint("xl") && size.width < parseBreakpoint("xxl")
  )
}
export const isXXl = (size: TWindowSize) => {
  return size.width > parseBreakpoint("xxl")
}

export const isXXXL = (size: TWindowSize) => {
  return size.width > parseBreakpoint("xxxl")
}

export const isBrowser = ![typeof window, typeof document].includes("undefined")

export const isMobileDevice = (screenSize: TWindowSize) => isSm(screenSize)

export const isSafari = () =>
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

export function isTouchDevice() {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  )
}

export const useIsMobileDevice = function() {
  const size = useWindowSizeLocal()

  const isMobile = useMemo(() => {
    const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobUserAgent = regex.test(navigator.userAgent)
    const canOrientate = !!window.orientation;
    return (isMobUserAgent || canOrientate);
  }, [size])

  return isMobile;
};

export const useIsLandscape = function() {
  const size = useWindowSizeLocal()

  const isLandscape = useMemo(() => {
    return window.orientation === -90 || window.orientation === 90
  }, [size])

  return isLandscape
}

export const useIsMobileLandscape = function() {
  const isMobile = useIsMobileDevice()
  const isLandscape = useIsLandscape()
  const isMobileLandscape = isMobile && isLandscape
  return isMobileLandscape
}